import React, { useState, useEffect, useRef } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { Trash2, AlertTriangle, X, User, Mail, Shield, AlertCircle, ChevronRight, Check, ImageIcon } from 'lucide-react'
import DashboardLayout from '../components/DashboardLayout'
import ChangePasswordModal from '../components/ChangePasswordModal'
import PlayerCardPreview from '../components/PlayerCardPreview'
import { useAuth } from '../context/AuthContext'
import { estMembreDeClub, estVraiFree } from '../utils/planAccess'
import { useCategoriesOptions } from '../constants/categories'
import { COMPETITION_LEVELS_BY_CATEGORY, ALL_COMPETITION_LEVELS } from '../constants/competitionLevels'
import SubscriptionManagement from '../components/SubscriptionManagement'
import BuyAnalysesModal from '../components/BuyAnalysesModal'
import api from '../services/api'
import { useSeason } from '../context/SeasonContext'
import { T, globalStyles } from '../theme'

const G = {
  bg:      T.bg,
  bg2:     T.surface,
  gold:    T.gold,
  goldD:   T.goldD,
  goldBg:  T.goldBg,
  goldBdr: T.goldBdr,
  mono:    T.mono,
  display: T.display,
  border:  T.ruleDark,
  muted:   T.muted2,
  text:    T.ink,
}

// ─── Profil coach — champs onboarding ────────────────────────────────────────

const DIPLOMAS_LIST   = ['CFI', 'DF', 'BMF (UEFA B)', 'BEF (UEFA A)', 'DES', 'BEPF (UEFA PRO)']
const EXPERIENCE_LIST = ['1 an', '2-5 ans', '+5 ans', '+10 ans']
const FILMING_LIST    = [
  { id: 'smartphone_hand',  label: 'Smartphone à la main'  },
  { id: 'smartphone_fixed', label: 'Smartphone fixe'        },
  { id: 'ai_camera',        label: 'Caméra intelligente'    },
  { id: 'drone',            label: 'Drone'                  },
  { id: 'multiple',         label: 'Plusieurs angles'       },
  { id: 'no_setup',         label: 'Pas de setup'          },
]

function ProfilCoachSection({ user, onSaved, showIdentity = true, identity, identityLoaded, onEditIdentity }) {
  const { categories } = useCategoriesOptions()
  const [editing, setEditing]       = React.useState(false)
  const [saving, setSaving]         = React.useState(false)
  const [diploma, setDiploma]       = React.useState(user?.profile_diploma || '')
  const [experience, setExperience] = React.useState(user?.experience || '')
  const [category, setCategory]     = React.useState(user?.team_category || '')
  const [filming, setFilming]       = React.useState(
    user?.filming_setup ? user.filming_setup.split(',') : []
  )

  React.useEffect(() => {
    setDiploma(user?.profile_diploma || '')
    setExperience(user?.experience || '')
    setCategory(user?.team_category || '')
    setFilming(user?.filming_setup ? user.filming_setup.split(',') : [])
  }, [user])

  const toggleFilming = (id) => setFilming(prev =>
    prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]
  )

  const handleSave = async () => {
    setSaving(true)
    try {
      await api.patch('/account/profile', {
        diploma,
        experience,
        team_category: category,
        filming_setup: filming.join(','),
      })
      if (onSaved) await onSaved()
      setEditing(false)
    } catch (e) { console.error(e) }
    finally { setSaving(false) }
  }

  const isComplete = diploma && experience && category && filming.length > 0
  const hasIdentity = !!identity
  const fullyComplete = showIdentity ? (isComplete && hasIdentity) : isComplete
  const chipStyle = (active) => ({
    padding: '6px 12px', fontFamily: G.mono, fontSize: 9, letterSpacing: '.06em',
    background: active ? G.goldBg : 'transparent',
    border: `1px solid ${active ? G.goldBdr : G.border}`,
    color: active ? G.gold : G.muted, cursor: 'pointer', transition: 'all .1s',
  })

  // Style sous-titres internes
  const subTitle = {
    fontFamily: G.mono, fontSize: 8, letterSpacing: '.16em',
    textTransform: 'uppercase', color: G.muted,
    display: 'flex', alignItems: 'center', gap: 8,
    marginBottom: 14,
  }

  return (
    <Section title="Mon profil de coach" accent={fullyComplete ? G.gold : 'rgba(239,68,68,0.5)'}>

      {/* ── Sous-section : INFORMATIONS ── */}
      <div style={subTitle}>
        <span style={{ width: 8, height: 1, background: G.muted, display: 'inline-block' }} />
        Informations
      </div>

      {!editing ? (
        <div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginBottom: 16 }}>
            {[
              { label: 'Diplôme',    value: diploma     || '—' },
              { label: 'Expérience', value: experience  || '—' },
              { label: 'Catégorie',  value: category    || '—' },
              { label: 'Tournage',   value: filming.length > 0 ? filming.map(f => FILMING_LIST.find(x => x.id === f)?.label || f).join(', ') : '—' },
            ].map(({ label, value }) => (
              <div key={label} style={{ display: 'flex', gap: 12, padding: '10px 14px', background: 'rgba(255,255,255,0.02)', border: `1px solid ${G.border}` }}>
                <span style={{ fontFamily: G.mono, fontSize: 8, letterSpacing: '.12em', textTransform: 'uppercase', color: G.muted, minWidth: 80 }}>{label}</span>
                <span style={{ fontFamily: G.mono, fontSize: 11, color: value === '—' ? G.muted : G.text }}>{value}</span>
              </div>
            ))}
          </div>
          {!isComplete && (
            <p style={{ fontFamily: G.mono, fontSize: 10, color: 'rgba(239,68,68,0.7)', marginBottom: 12, lineHeight: 1.5 }}>
              Informations incomplètes. Ces données permettent d'affiner tes recommandations de séances.
            </p>
          )}
          <button onClick={() => setEditing(true)} style={{ padding: '9px 18px', background: 'transparent', border: `1px solid ${G.border}`, color: G.muted, fontFamily: G.mono, fontSize: 9, letterSpacing: '.1em', textTransform: 'uppercase', cursor: 'pointer' }}>
            Modifier
          </button>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
          <div>
            <label style={{ fontFamily: G.mono, fontSize: 8, letterSpacing: '.14em', textTransform: 'uppercase', color: G.muted, display: 'block', marginBottom: 8 }}>Diplôme</label>
            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
              {DIPLOMAS_LIST.map(d => <button key={d} onClick={() => setDiploma(d)} style={chipStyle(diploma === d)}>{d}</button>)}
            </div>
          </div>
          <div>
            <label style={{ fontFamily: G.mono, fontSize: 8, letterSpacing: '.14em', textTransform: 'uppercase', color: G.muted, display: 'block', marginBottom: 8 }}>Expérience</label>
            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
              {EXPERIENCE_LIST.map(e => <button key={e} onClick={() => setExperience(e)} style={chipStyle(experience === e)}>{e}</button>)}
            </div>
          </div>
          <div>
            <label style={{ fontFamily: G.mono, fontSize: 8, letterSpacing: '.14em', textTransform: 'uppercase', color: G.muted, display: 'block', marginBottom: 8 }}>Catégorie principale</label>
            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
              {categories.map(c => <button key={c} type="button" onClick={() => setCategory(c)} style={chipStyle(category === c)}>{c}</button>)}
            </div>
          </div>
          <div>
            <label style={{ fontFamily: G.mono, fontSize: 8, letterSpacing: '.14em', textTransform: 'uppercase', color: G.muted, display: 'block', marginBottom: 8 }}>Setup de tournage</label>
            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
              {FILMING_LIST.map(f => <button key={f.id} onClick={() => toggleFilming(f.id)} style={chipStyle(filming.includes(f.id))}>{f.label}</button>)}
            </div>
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <button onClick={() => setEditing(false)} style={{ padding: '9px 16px', background: 'transparent', border: `1px solid ${G.border}`, color: G.muted, fontFamily: G.mono, fontSize: 9, letterSpacing: '.1em', textTransform: 'uppercase', cursor: 'pointer' }}>Annuler</button>
            <button onClick={handleSave} disabled={saving} style={{ padding: '9px 20px', background: G.gold, border: 'none', color: '#fff', fontFamily: G.mono, fontSize: 9, letterSpacing: '.1em', textTransform: 'uppercase', fontWeight: 700, cursor: 'pointer' }}>
              {saving ? 'Enregistrement...' : 'Enregistrer'}
            </button>
          </div>
        </div>
      )}

      {/* ── Sous-section : IDENTITÉ DE JEU ── */}
      {showIdentity && (
        <>
          <div style={{ height: 1, background: G.border, margin: '28px 0 24px' }} />

          <div style={subTitle}>
            <span style={{ width: 8, height: 1, background: G.muted, display: 'inline-block' }} />
            Identité de jeu
          </div>

          {!identityLoaded ? (
            <div style={{ fontFamily: G.mono, fontSize: 11, color: G.muted }}>Chargement...</div>
          ) : identity ? (
            <IdentitySummary identity={identity} onEdit={onEditIdentity} />
          ) : (
            <div>
              <p style={{ fontFamily: G.mono, fontSize: 11, color: G.muted, lineHeight: 1.7, margin: '0 0 16px 0' }}>
                Dis-nous comment tu travailles : vision tactique, posture, identité de jeu. Tes recommandations seront adaptées.
              </p>
              <button
                onClick={onEditIdentity}
                style={{
                  background: G.gold, border: 'none',
                  padding: '10px 20px', fontFamily: G.mono, fontSize: 9,
                  letterSpacing: '.12em', textTransform: 'uppercase',
                  color: '#fff', cursor: 'pointer', fontWeight: 700,
                  display: 'flex', alignItems: 'center', gap: 8,
                }}
              >
                Compléter mon identité <ChevronRight size={11} />
              </button>
            </div>
          )}
        </>
      )}

    </Section>
  )
}

// ─── Composant Section ────────────────────────────────────────────────────────

function Section({ title, accent, children }) {
  return (
    <div style={{ background: G.bg2, border: `1px solid ${G.border}`, borderTop: `2px solid ${accent || G.border}`, padding: '28px' }}>
      <div style={{ fontFamily: G.mono, fontSize: 9, letterSpacing: '.18em', textTransform: 'uppercase', color: G.gold, display: 'flex', alignItems: 'center', gap: 8, marginBottom: 20 }}>
        <span style={{ width: 12, height: 1, background: G.gold, display: 'inline-block' }} />{title}
      </div>
      {children}
    </div>
  )
}

// ─── Chip sélectionnable ──────────────────────────────────────────────────────

function Chip({ label, active, onClick }) {
  return (
    <button onClick={onClick} style={{
      padding: '7px 14px',
      borderRadius: 99,
      border: active ? `1.5px solid ${G.gold}` : `1px solid ${G.border}`,
      background: active ? G.gold : 'transparent',
      color: active ? '#fff' : G.text,
      fontFamily: G.mono,
      fontSize: 11,
      letterSpacing: '.03em',
      cursor: 'pointer',
      transition: 'all .15s',
      fontWeight: active ? 600 : 400,
    }}>{label}</button>
  )
}

// ─── Carte sélectionnable (grand format) ─────────────────────────────────────

function CardChip({ label, desc, active, onClick }) {
  return (
    <button onClick={onClick} style={{
      padding: '12px 14px',
      border: active ? `1.5px solid ${G.gold}` : `1px solid ${G.border}`,
      background: active ? 'rgba(79,142,247,0.06)' : 'transparent',
      cursor: 'pointer',
      textAlign: 'left',
      transition: 'all .15s',
      flex: 1,
      minWidth: 140,
    }}>
      <div style={{ fontFamily: G.mono, fontSize: 12, color: G.text, fontWeight: active ? 600 : 400, marginBottom: desc ? 4 : 0 }}>{label}</div>
      {desc && <div style={{ fontFamily: G.mono, fontSize: 10, color: G.muted, lineHeight: 1.5 }}>{desc}</div>}
    </button>
  )
}

// ─── Curseur custom ───────────────────────────────────────────────────────────

function RangeSlider({ label, min, max, step, value, onChange, formatValue }) {
  const trackRef = useRef(null)
  const pct = ((value - min) / (max - min)) * 100

  function computeVal(clientX) {
    const rect = trackRef.current.getBoundingClientRect()
    let p = (clientX - rect.left) / rect.width
    p = Math.max(0, Math.min(1, p))
    let raw = min + p * (max - min)
    let stepped = Math.round(raw / step) * step
    return Math.max(min, Math.min(max, stepped))
  }

  function onMouseDown(e) {
    onChange(computeVal(e.clientX))
    const onMove = e2 => onChange(computeVal(e2.clientX))
    const onUp = () => { document.removeEventListener('mousemove', onMove); document.removeEventListener('mouseup', onUp) }
    document.addEventListener('mousemove', onMove)
    document.addEventListener('mouseup', onUp)
    e.preventDefault()
  }

  function onTouchStart(e) {
    onChange(computeVal(e.touches[0].clientX))
    const onMove = e2 => onChange(computeVal(e2.touches[0].clientX))
    const onEnd = () => { document.removeEventListener('touchmove', onMove); document.removeEventListener('touchend', onEnd) }
    document.addEventListener('touchmove', onMove, { passive: true })
    document.addEventListener('touchend', onEnd)
  }

  return (
    <div style={{ marginBottom: 24 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 10 }}>
        <div style={{ fontFamily: G.mono, fontSize: 11, letterSpacing: '.04em', color: G.muted }}>{label}</div>
        <div style={{ fontFamily: G.mono, fontSize: 13, fontWeight: 600, color: G.gold }}>{formatValue(value)}</div>
      </div>
      <div
        ref={trackRef}
        onMouseDown={onMouseDown}
        onTouchStart={onTouchStart}
        style={{ position: 'relative', height: 36, display: 'flex', alignItems: 'center', cursor: 'pointer', userSelect: 'none' }}
      >
        {/* Piste fond */}
        <div style={{ position: 'absolute', left: 0, right: 0, height: 3, background: G.border }} />
        {/* Piste active */}
        <div style={{ position: 'absolute', left: 0, width: `${pct}%`, height: 3, background: G.gold, transition: 'width .08s' }} />
        {/* Thumb */}
        <div style={{
          position: 'absolute',
          left: `${pct}%`,
          transform: 'translate(-50%, -50%)',
          top: '50%',
          width: 20,
          height: 20,
          borderRadius: '50%',
          background: G.gold,
          boxShadow: '0 1px 4px rgba(0,0,0,.2)',
          transition: 'left .08s',
          zIndex: 2,
        }} />
      </div>
    </div>
  )
}

// ─── Modale identité de coach ─────────────────────────────────────────────────

const STEPS = [
  'Style de jeu',
  'Objectifs',
  'Ton staff',
  'Conditions terrain',
]

const DUREE_LABELS = ['1h', '1h30', '2h']

function CoachIdentityModal({ onClose, onSave, initialData }) {
  const [step, setStep] = useState(0)
  const [saving, setSaving] = useState(false)

  // État formulaire
  const [style, setStyle] = useState(initialData?.style || '')
  const [principesOff, setPrincipesOff] = useState(initialData?.principes_off || [])
  const [principeesDef, setPrincipesDef] = useState(initialData?.principes_def || [])
  const [prioriteFormation, setPrioriteFormation] = useState(initialData?.priorite_formation || '')
  const [objectifSaison, setObjectifSaison] = useState(initialData?.objectif_saison || '')
  const [profilCoach, setProfilCoach] = useState(initialData?.profil_coach || '')
  const [staff, setStaff] = useState(initialData?.staff || '')
  const [groupe, setGroupe] = useState(initialData?.groupe || '')
  const [seances, setSeances] = useState(initialData?.seances_semaine ?? 2)
  const [duree, setDuree] = useState(() => {
    const d = initialData?.duree_seance
    if (d === '1h') return 0
    if (d === '2h') return 2
    return 1
  })
  const [effectif, setEffectif] = useState(initialData?.effectif ?? 18)
  const [terrain, setTerrain] = useState(initialData?.terrain || '')
  const [materiel, setMateriel] = useState(initialData?.materiel || [])

  function toggleMulti(val, list, setList, max) {
    if (list.includes(val)) { setList(list.filter(v => v !== val)); return }
    if (list.length >= max) return
    setList([...list, val])
  }

  async function handleSave() {
    setSaving(true)
    try {
      const payload = {
        style,
        principes_off: principesOff,
        principes_def: principeesDef,
        priorite_formation: prioriteFormation,
        objectif_saison: objectifSaison,
        profil_coach: profilCoach,
        staff,
        groupe,
        seances_semaine: seances,
        duree_seance: DUREE_LABELS[duree],
        effectif,
        terrain,
        materiel,
      }
      await api.put('/account/coach-identity/', payload)
      onSave(payload)
    } catch {
      // silencieux — on ferme quand même
      onSave(null)
    } finally {
      setSaving(false)
    }
  }

  const isMobile = typeof window !== 'undefined' && window.innerWidth < 768

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(10,9,8,0.92)', display: 'flex', alignItems: 'flex-start', justifyContent: 'center', zIndex: 1000, overflowY: 'auto', padding: isMobile ? '0' : '40px 20px' }}>
      <div style={{ background: G.bg, width: '100%', maxWidth: 620, minHeight: isMobile ? '100vh' : 'auto', border: isMobile ? 'none' : `1px solid ${G.border}`, borderTop: `2px solid ${G.gold}`, display: 'flex', flexDirection: 'column' }}>

        {/* Header modale */}
        <div style={{ padding: '20px 24px 16px', borderBottom: `1px solid ${G.border}`, display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexShrink: 0 }}>
          <div>
            <div style={{ fontFamily: G.mono, fontSize: 9, letterSpacing: '.18em', textTransform: 'uppercase', color: G.gold, marginBottom: 4 }}>
              Mon identité de coach
            </div>
            <div style={{ fontFamily: G.display, fontSize: 18, textTransform: 'uppercase', color: G.text }}>
              {STEPS[step]}
            </div>
          </div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 4 }}>
            <X size={16} color={G.muted} />
          </button>
        </div>

        {/* Barre de progression */}
        <div style={{ height: 2, background: G.border, flexShrink: 0 }}>
          <div style={{ height: '100%', background: G.gold, width: `${((step + 1) / STEPS.length) * 100}%`, transition: 'width .3s' }} />
        </div>

        {/* Indicateurs étapes */}
        <div style={{ display: 'flex', padding: '12px 24px', gap: 8, borderBottom: `1px solid ${G.border}`, flexShrink: 0 }}>
          {STEPS.map((s, i) => (
            <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 6, opacity: i > step ? 0.35 : 1 }}>
              <div style={{
                width: 18, height: 18, borderRadius: '50%',
                background: i < step ? G.gold : i === step ? 'transparent' : 'transparent',
                border: i < step ? 'none' : `1px solid ${i === step ? G.gold : G.border}`,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                flexShrink: 0,
              }}>
                {i < step
                  ? <Check size={10} color="#fff" />
                  : <span style={{ fontFamily: G.mono, fontSize: 9, color: i === step ? G.gold : G.muted }}>{i + 1}</span>
                }
              </div>
              {i < STEPS.length - 1 && <div style={{ width: 16, height: 1, background: G.border }} />}
            </div>
          ))}
        </div>

        {/* Contenu */}
        <div style={{ padding: '24px', flex: 1, overflowY: 'auto' }}>

          {/* ── Étape 0 : Style de jeu ── */}
          {step === 0 && (
            <>
              <div style={{ marginBottom: 28 }}>
                <div style={{ fontFamily: G.mono, fontSize: 11, color: G.text, marginBottom: 12, fontWeight: 600 }}>Comment tu veux que ton équipe joue ?</div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
                  {[
                    { label: 'Possession et construction', desc: 'Sortir proprement, garder le ballon' },
                    { label: 'Transition rapide', desc: 'Exploiter vite les espaces après récupération' },
                    { label: 'Pressing haut', desc: 'Récupérer haut, ne pas laisser jouer' },
                    { label: 'Bloc défensif solide', desc: 'Défendre bas, compacité, contres' },
                    { label: 'Jeu direct', desc: 'Long ballon, second ballon, intensité' },
                    { label: 'Mixte selon situation', desc: 'S\'adapter à l\'adversaire et au score' },
                  ].map(({ label, desc }) => (
                    <CardChip key={label} label={label} desc={desc} active={style === label} onClick={() => setStyle(label)} />
                  ))}
                </div>
              </div>

              <div style={{ marginBottom: 28 }}>
                <div style={{ fontFamily: G.mono, fontSize: 11, color: G.text, marginBottom: 4, fontWeight: 600 }}>Avec le ballon : tes principes non négociables</div>
                <div style={{ fontFamily: G.mono, fontSize: 10, color: G.muted, marginBottom: 12 }}>Choisis jusqu'à 3 éléments</div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                  {['Sortir depuis l\'arrière', 'Jouer entre les lignes', 'Attaquer vite les espaces', 'Trouver les intervalles', 'Centres et finitions', 'Créer la supériorité numérique'].map(v => (
                    <Chip key={v} label={v} active={principesOff.includes(v)} onClick={() => toggleMulti(v, principesOff, setPrincipesOff, 3)} />
                  ))}
                </div>
              </div>

              <div>
                <div style={{ fontFamily: G.mono, fontSize: 11, color: G.text, marginBottom: 4, fontWeight: 600 }}>Sans le ballon : tes principes non négociables</div>
                <div style={{ fontFamily: G.mono, fontSize: 10, color: G.muted, marginBottom: 12 }}>Choisis jusqu'à 3 éléments</div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                  {['Presser haut dès la perte', 'Repli rapide et bloc organisé', 'Zone compacte', 'Marquage individuel', 'Pressing déclenché sur signal', 'Protéger l\'axe en priorité'].map(v => (
                    <Chip key={v} label={v} active={principeesDef.includes(v)} onClick={() => toggleMulti(v, principeesDef, setPrincipesDef, 3)} />
                  ))}
                </div>
              </div>
            </>
          )}

          {/* ── Étape 1 : Objectifs ── */}
          {step === 1 && (
            <>
              <div style={{ marginBottom: 28 }}>
                <div style={{ fontFamily: G.mono, fontSize: 11, color: G.text, marginBottom: 12, fontWeight: 600 }}>Ta priorité de formation cette saison</div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                  {['Intelligence tactique', 'Technique individuelle', 'Intensité et engagement', 'Prise de décision rapide', 'Cohésion collective', 'Tout équilibré'].map(v => (
                    <Chip key={v} label={v} active={prioriteFormation === v} onClick={() => setPrioriteFormation(v)} />
                  ))}
                </div>
              </div>

              <div style={{ marginBottom: 28 }}>
                <div style={{ fontFamily: G.mono, fontSize: 11, color: G.text, marginBottom: 4, fontWeight: 600 }}>Ton objectif cette saison</div>
                <div style={{ fontFamily: G.mono, fontSize: 10, color: G.muted, marginBottom: 12 }}>Honnêtement, c'est quoi le succès en fin de saison ?</div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
                  {[
                    { label: 'Montée', desc: 'Finir dans les places qualificatives' },
                    { label: 'Maintien', desc: 'Assurer le maintien sereinement' },
                    { label: 'Progression collective', desc: 'Faire progresser le jeu de l\'équipe' },
                    { label: 'Formation individuelle', desc: 'Faire progresser chaque joueur' },
                    { label: 'Régularité', desc: 'Avoir un groupe fiable semaine après semaine' },
                    { label: 'Groupe soudé', desc: 'Terminer la saison avec une vraie cohésion' },
                  ].map(({ label, desc }) => (
                    <CardChip key={label} label={label} desc={desc} active={objectifSaison === label} onClick={() => setObjectifSaison(label)} />
                  ))}
                </div>
              </div>

              <div>
                <div style={{ fontFamily: G.mono, fontSize: 11, color: G.text, marginBottom: 4, fontWeight: 600 }}>Comment tu te définis en tant que coach ?</div>
                <div style={{ fontFamily: G.mono, fontSize: 10, color: G.muted, marginBottom: 12 }}>Pas un jugement, juste pour calibrer tes recommandations</div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                  {['Plutôt technicien', 'Plutôt tacticien', 'Plutôt manager humain', 'Un mix des trois'].map(v => (
                    <Chip key={v} label={v} active={profilCoach === v} onClick={() => setProfilCoach(v)} />
                  ))}
                </div>
              </div>
            </>
          )}

          {/* ── Étape 2 : Staff ── */}
          {step === 2 && (
            <>
              <div style={{ marginBottom: 28 }}>
                <div style={{ fontFamily: G.mono, fontSize: 11, color: G.text, marginBottom: 4, fontWeight: 600 }}>Ton staff</div>
                <div style={{ fontFamily: G.mono, fontSize: 10, color: G.muted, marginBottom: 12 }}>Ça change la complexité des séances qu'on te propose</div>
                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                  {[
                    { label: 'Seul', desc: 'Tu gères tout de A à Z' },
                    { label: 'Avec un adjoint', desc: 'Un second pour animer' },
                    { label: 'Staff complet', desc: 'Prépa physique, adjoint...' },
                  ].map(({ label, desc }) => (
                    <CardChip key={label} label={label} desc={desc} active={staff === label} onClick={() => setStaff(label)} />
                  ))}
                </div>
              </div>

              <div>
                <div style={{ fontFamily: G.mono, fontSize: 11, color: G.text, marginBottom: 4, fontWeight: 600 }}>Ton groupe cette saison</div>
                <div style={{ fontFamily: G.mono, fontSize: 10, color: G.muted, marginBottom: 12 }}>Décris-le honnêtement</div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                  {['Homogène et soudé', 'Bon niveau mais irrégulier', 'En reconstruction', 'Profils très différents', 'Jeunes en formation pure'].map(v => (
                    <Chip key={v} label={v} active={groupe === v} onClick={() => setGroupe(v)} />
                  ))}
                </div>
              </div>
            </>
          )}

          {/* ── Étape 3 : Conditions ── */}
          {step === 3 && (
            <>
              <div style={{ marginBottom: 28 }}>
                <div style={{ fontFamily: G.mono, fontSize: 11, color: G.text, marginBottom: 20, fontWeight: 600 }}>Tes conditions de travail</div>

                <RangeSlider
                  label="Séances par semaine"
                  min={1} max={7} step={1} value={seances}
                  onChange={setSeances}
                  formatValue={v => `${v} séance${v > 1 ? 's' : ''}`}
                />
                <RangeSlider
                  label="Durée moyenne d'une séance"
                  min={0} max={2} step={1} value={duree}
                  onChange={setDuree}
                  formatValue={v => DUREE_LABELS[Math.round(v)]}
                />
                <RangeSlider
                  label="Effectif habituel"
                  min={10} max={25} step={1} value={effectif}
                  onChange={setEffectif}
                  formatValue={v => `${v} joueurs`}
                />
              </div>

              <div style={{ marginBottom: 28 }}>
                <div style={{ fontFamily: G.mono, fontSize: 11, color: G.text, marginBottom: 12, fontWeight: 600 }}>Type de terrain</div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                  {['Synthétique', 'Herbe naturelle', 'Les deux'].map(v => (
                    <Chip key={v} label={v} active={terrain === v} onClick={() => setTerrain(v)} />
                  ))}
                </div>
              </div>

              <div>
                <div style={{ fontFamily: G.mono, fontSize: 11, color: G.text, marginBottom: 4, fontWeight: 600 }}>Matériel disponible</div>
                <div style={{ fontFamily: G.mono, fontSize: 10, color: G.muted, marginBottom: 12 }}>Sélectionne ce que tu as régulièrement</div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                  {['Cônes et plots', 'Chasubles', 'Petits buts', 'Mannequins', 'Buts réglementaires', 'Haies', 'Échelles de rythme'].map(v => (
                    <Chip key={v} label={v} active={materiel.includes(v)} onClick={() => toggleMulti(v, materiel, setMateriel, 10)} />
                  ))}
                </div>
              </div>
            </>
          )}
        </div>

        {/* Footer navigation */}
        <div style={{ padding: '16px 24px', borderTop: `1px solid ${G.border}`, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexShrink: 0, background: G.bg }}>
          <button
            onClick={() => step > 0 ? setStep(step - 1) : onClose()}
            style={{ background: 'none', border: `1px solid ${G.border}`, padding: '9px 18px', fontFamily: G.mono, fontSize: 9, letterSpacing: '.1em', textTransform: 'uppercase', color: G.muted, cursor: 'pointer' }}
          >
            {step === 0 ? 'Annuler' : '← Retour'}
          </button>

          <div style={{ fontFamily: G.mono, fontSize: 9, color: G.muted }}>
            {step + 1} / {STEPS.length}
          </div>

          {step < STEPS.length - 1 ? (
            <button
              onClick={() => setStep(step + 1)}
              style={{ background: G.gold, border: 'none', padding: '10px 24px', fontFamily: G.mono, fontSize: 9, letterSpacing: '.1em', textTransform: 'uppercase', color: '#fff', cursor: 'pointer', fontWeight: 700 }}
            >
              Suivant →
            </button>
          ) : (
            <button
              onClick={handleSave}
              disabled={saving}
              style={{ background: saving ? G.goldBg : G.gold, border: 'none', padding: '10px 24px', fontFamily: G.mono, fontSize: 9, letterSpacing: '.1em', textTransform: 'uppercase', color: saving ? G.gold : '#fff', cursor: saving ? 'not-allowed' : 'pointer', fontWeight: 700, display: 'flex', alignItems: 'center', gap: 8 }}
            >
              {saving ? 'Enregistrement...' : <><Check size={11} /> Enregistrer mon profil</>}
            </button>
          )}
        </div>
      </div>
    </div>
  )
}

// ─── Résumé identité ──────────────────────────────────────────────────────────

function IdentitySummary({ identity, onEdit }) {
  const items = [
    identity?.style,
    identity?.priorite_formation,
    identity?.objectif_saison,
    identity?.staff,
  ].filter(Boolean)

  return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
      <div style={{ flex: 1 }}>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 8 }}>
          {items.map(item => (
            <span key={item} style={{
              fontFamily: G.mono, fontSize: 10,
              padding: '3px 10px',
              background: G.goldBg,
              border: `1px solid ${G.goldBdr}`,
              color: G.gold,
            }}>{item}</span>
          ))}
        </div>
        <div style={{ fontFamily: G.mono, fontSize: 10, color: G.muted }}>
          {identity?.seances_semaine} séance{identity?.seances_semaine > 1 ? 's' : ''}/sem · {identity?.duree_seance} · {identity?.effectif} joueurs · {identity?.terrain}
        </div>
      </div>
      <button onClick={onEdit} style={{
        background: 'none', border: `1px solid ${G.border}`,
        padding: '8px 14px', fontFamily: G.mono, fontSize: 9,
        letterSpacing: '.1em', textTransform: 'uppercase',
        color: G.muted, cursor: 'pointer', flexShrink: 0,
        display: 'flex', alignItems: 'center', gap: 6,
      }}>
        Modifier <ChevronRight size={10} />
      </button>
    </div>
  )
}

// ─── Page principale ──────────────────────────────────────────────────────────

export default function CoachSettings() {
  const { user, refreshUser } = useAuth()
  const location = useLocation()
  const navigate = useNavigate()
  const { currentSeason, setSelectedSeason } = useSeason()
  const flashMessage = location.state?.flash || ''
  const [isMobile, setIsMobile] = useState(() => typeof window !== 'undefined' && window.innerWidth < 768)
  const [archivedSeasons, setArchivedSeasons] = useState([])
  const [showPwdModal, setShowPwdModal] = useState(false)
  const [showDeleteModal, setShowDeleteModal] = useState(false)
  const [deleteConfirm, setDeleteConfirm] = useState('')
  const [deleteLoading, setDeleteLoading] = useState(false)
  const [deleteError, setDeleteError] = useState('')
  const [isTrialing, setIsTrialing] = useState(null)

  // BANDEAU-SANS-QUOTA-001 · un lien `#abonnement` doit ARRIVER sur l'offre.
  //
  // Le navigateur ne defile pas tout seul vers une ancre rendue APRES le
  // premier paint · cette page charge son club, ses saisons et sa carte joueur
  // avant d'afficher la section. On attend donc que l'element existe, avec un
  // nombre de tentatives borne · un defilement qui n'arrive jamais vaut mieux
  // qu'une boucle qui tourne pour toujours sur une page ou l'ancre n'existe
  // pas (un membre de club n'a pas de section Abonnement editable).
  useEffect(() => {
    if (location.hash !== '#abonnement') return
    let restant = 40
    const t = setInterval(() => {
      const cible = document.getElementById('abonnement')
      if (cible) {
        clearInterval(t)
        cible.scrollIntoView({ behavior: 'smooth', block: 'start' })
      } else if (--restant <= 0) {
        clearInterval(t)
      }
    }, 100)
    return () => clearInterval(t)
  }, [location.hash])

  // Analyses
  const [creditsData, setCreditsData] = useState(null)
  const [showBuyAnalysesModal, setShowBuyAnalysesModal] = useState(false)

  // Identité de coach
  const [showIdentityModal, setShowIdentityModal] = useState(false)
  const [coachIdentity, setCoachIdentity] = useState(null)
  const [identityLoaded, setIdentityLoaded] = useState(false)

  // Identité du club (nom + logo) — éditable par le coach solo propriétaire
  // de son club (cf. condition !isClubMember plus bas).
  const [clubName, setClubName] = useState(user?.club_name || '')
  const [logoUrl, setLogoUrl] = useState(user?.club_logo || '')
  const [clubSaving, setClubSaving] = useState(false)
  const [clubSaved, setClubSaved] = useState(false)
  const [clubError, setClubError] = useState('')
  const clubLockRef = useRef(false)

  // Niveau de mon équipe (coach membre uniquement)
  const [teamLevel, setTeamLevel] = useState('')
  const [teamLevelSaving, setTeamLevelSaving] = useState('idle') // 'idle'|'saving'|'saved'
  const [clubColors, setClubColors] = useState({ primary: '#4F8EF7', secondary: '#0f0f0d' })

  useEffect(() => {
    if (user?.club_logo) setLogoUrl(user.club_logo)
  }, [user?.club_logo])
  useEffect(() => {
    if (user?.club_name) setClubName(user.club_name)
  }, [user?.club_name])

  useEffect(() => {
    if (!user?.club_id || !user?.managed_category) return
    api.get('/club/competition-levels')
      .then(({ data }) => {
        const entry = data.find(r => r.category === user.managed_category)
        setTeamLevel(entry?.competition_level || '')
      })
      .catch(() => {})
  }, [user?.managed_category])

  useEffect(() => {
    if (!user?.club_id) return
    api.get('/club/me')
      .then(({ data }) => setClubColors({
        primary: data.primary_color || '#4F8EF7',
        secondary: data.secondary_color || '#0f0f0d',
      }))
      .catch(() => {})
  }, [user?.club_id])

  const handleTeamLevelSave = async () => {
    const cat = user.managed_category
    setTeamLevelSaving('saving')
    try {
      await api.patch(`/club/competition-levels/${encodeURIComponent(cat)}`, {
        competition_level: teamLevel || null,
      })
      setTeamLevelSaving('saved')
      setTimeout(() => setTeamLevelSaving('idle'), 2500)
    } catch (e) {
      setTeamLevelSaving('idle')
    }
  }

  const handleSaveClub = async () => {
    if (!clubName.trim()) { setClubError('Donne un nom à ton club'); return }
    if (clubLockRef.current) return
    clubLockRef.current = true
    setClubSaving(true)
    setClubSaved(false)
    setClubError('')
    try {
      // Un seul round-trip : nom + logo. refreshUser propage club_name et
      // club_logo au cache pour que DashboardHome affiche le bon nom.
      await api.patch('/club/me', { name: clubName.trim(), logo_url: logoUrl })
      if (refreshUser) await refreshUser()
      setClubSaved(true)
      setTimeout(() => setClubSaved(false), 3000)
    } catch (e) {
      console.error(e)
      setClubError('Erreur lors de l\'enregistrement')
    } finally {
      clubLockRef.current = false
      setClubSaving(false)
    }
  }

  useEffect(() => {
    // A8 · sans ça, les crédits d'un membre de club n'étaient jamais chargés,
    // donc le bloc Analyses restait vide quoi qu'il arrive.
    if (estVraiFree(user)) return
    api.get('/matches/credits/status')
      .then(({ data }) => setCreditsData(data))
      .catch(() => {})
    // `user` entier, et plus `user?.plan` seul · la garde lit desormais aussi
    // club_id, managed_category et stripe_subscription_id.
  }, [user])

  const isClubMember = estMembreDeClub(user)
  const isClubAdmin = !!(user?.club_id && (user?.plan === 'CLUB' || user?.plan === 'CLUB_PRO') && user?.role === 'ADMIN')
  // Identité affichée pour tous les coachs (membres club inclus) sauf DS admin
  const showIdentity = !isClubAdmin

  useEffect(() => {
    if (!showIdentity) return
    const fetchIdentity = async () => {
      try {
        const { data } = await api.get('/account/coach-identity/')
        setCoachIdentity(data.identity)
      } catch {
        setCoachIdentity(null)
      } finally {
        setIdentityLoaded(true)
      }
    }
    fetchIdentity()
  }, [showIdentity])

  useEffect(() => {
    if (isClubMember) return
    const fetchTrialStatus = async () => {
      try {
        const { data } = await api.get('/subscription/trial-status')
        setIsTrialing(data.trial_active === true)
      } catch {
        setIsTrialing(false)
      }
    }
    fetchTrialStatus()
  }, [isClubMember])

  const getPlanLabel = () => {
    if (isClubMember) return 'Membre club · accès inclus'
    const p = (user?.plan || 'FREE').toUpperCase()
    if (p === 'FREE')     return 'Free · Gratuit'
    if (p === 'STARTER')  return 'Starter (archivé)'
    if (p === 'CLUB' || p === 'CLUB_PRO') return 'Club · Offre sur mesure · 8 analyses/mois'
    if (isTrialing === null) return 'Coach+'
    // Pendant l'essai le quota vaut 1 et non 3 (backend : constants.TRIAL_QUOTAS).
    // Ce libelle etait le seul des six a ne pas mentir, faute d'afficher un
    // chiffre pendant l'essai ; il le dit maintenant explicitement.
    return isTrialing
      ? 'Coach+ · Essai en cours · 1 analyse'
      : 'Coach+ · 39 €/mois · 3 analyses/mois'
  }

  const handleDeleteAccount = async () => {
    if (deleteConfirm !== 'SUPPRIMER') return
    setDeleteLoading(true)
    try {
      const token = localStorage.getItem('insightball_token')
      const API = import.meta.env.VITE_API_URL || 'https://backend-pued.onrender.com/api'
      const res = await fetch(`${API}/account/delete`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` }
      })
      if (!res.ok) throw new Error()
      localStorage.removeItem('insightball_token')
      window.location.href = '/'
    } catch {
      setDeleteError('Erreur lors de la suppression')
      setDeleteLoading(false)
    }
  }

  function handleIdentitySaved(payload) {
    if (payload) setCoachIdentity(payload)
    setShowIdentityModal(false)
  }

  useEffect(() => {
    const check = () => setIsMobile(window.innerWidth < 768)
    check(); window.addEventListener('resize', check)
    return () => window.removeEventListener('resize', check)
  }, [])

  useEffect(() => {
    api.get('/meta/seasons/')
      .then(({ data }) => {
        const past = (data.seasons || []).filter(s => s < currentSeason)
        setArchivedSeasons(past)
      })
      .catch(() => {})
  }, [currentSeason])

  return (
    <DashboardLayout>
      <style>{globalStyles}</style>

      {/* Header */}
      <div style={{ marginBottom: 32 }}>
        <div style={{ fontFamily: G.mono, fontSize: 9, letterSpacing: '.2em', textTransform: 'uppercase', color: G.gold, display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
          <span style={{ width: 16, height: 1, background: G.gold, display: 'inline-block' }} />Paramètres
        </div>
        <h1 style={{ fontFamily: G.display, fontSize: isMobile ? 28 : 52, textTransform: 'uppercase', lineHeight: .88, letterSpacing: '.01em', color: G.text, margin: 0, overflowWrap: 'break-word', wordBreak: 'break-word' }}>
          Mon<br /><span style={{ color: G.gold }}>compte.</span>
        </h1>
      </div>

      {flashMessage && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '12px 16px', marginBottom: 16, background: 'rgba(79,142,247,0.07)', border: '1px solid rgba(79,142,247,0.25)', maxWidth: isMobile ? '100%' : 600 }}>
          <AlertCircle size={13} color={G.gold} style={{ flexShrink: 0 }} />
          <span style={{ fontFamily: G.mono, fontSize: 10, color: G.gold, letterSpacing: '.04em' }}>{flashMessage}</span>
        </div>
      )}

      <div style={{ maxWidth: isMobile ? '100%' : 600, display: 'flex', flexDirection: 'column', gap: 1, background: G.border }}>

        {/* Infos compte */}
        <Section title="Informations" accent={G.gold}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            {[
              { icon: User,   label: 'Nom',   value: user?.name  || '—' },
              { icon: Mail,   label: 'Email', value: user?.email || '—' },
              { icon: Shield, label: 'Plan',  value: getPlanLabel() },
            ].map(({ icon: Icon, label, value }) => (
              <div key={label} style={{ display: 'flex', alignItems: 'center', gap: 14, padding: '14px 16px', background: 'rgba(255,255,255,0.02)', border: `1px solid ${G.border}` }}>
                <div style={{ width: 32, height: 32, background: G.goldBg, border: `1px solid ${G.goldBdr}`, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                  <Icon size={13} color={G.gold} />
                </div>
                <div>
                  <div style={{ fontFamily: G.mono, fontSize: 8, letterSpacing: '.14em', textTransform: 'uppercase', color: G.muted, marginBottom: 4 }}>{label}</div>
                  <div style={{ fontFamily: G.mono, fontSize: 13, color: G.text }}>{value}</div>
                </div>
              </div>
            ))}
            <button
              onClick={() => setShowPwdModal(true)}
              style={{ fontFamily: G.mono, fontSize: 10, fontWeight: 600, letterSpacing: '.08em', textTransform: 'uppercase', color: G.gold, background: 'transparent', border: `1px solid ${G.goldBdr}`, padding: '9px 16px', cursor: 'pointer', alignSelf: 'flex-start', transition: 'background .15s' }}
              onMouseEnter={e => { e.currentTarget.style.background = G.goldBg }}
              onMouseLeave={e => { e.currentTarget.style.background = 'transparent' }}
            >
              Modifier mon mot de passe
            </button>
          </div>
        </Section>

        {/* Profil coach — champs onboarding éditables */}
        <ProfilCoachSection
          user={user}
          onSaved={refreshUser}
          showIdentity={showIdentity}
          identity={coachIdentity}
          identityLoaded={identityLoaded}
          onEditIdentity={() => setShowIdentityModal(true)}
        />

        {/* Niveau de mon équipe — visible uniquement pour les coachs membres */}
        {isClubMember && (
          <Section title="Niveau de mon équipe" accent={G.gold}>
            <div style={{ display: 'flex', flexDirection: isMobile ? 'column' : 'row', gap: 24, alignItems: 'flex-start' }}>
              {/* Formulaire */}
              <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 12 }}>
                <div>
                  <div style={{
                    display: 'inline-flex', alignItems: 'center', gap: 6,
                    fontFamily: G.mono, fontSize: 8, letterSpacing: '.14em', textTransform: 'uppercase',
                    color: G.gold, padding: '5px 10px',
                    border: `1px solid ${G.goldBdr}`, background: G.goldBg,
                    marginBottom: 6,
                  }}>
                    Équipe&nbsp;: {user.managed_category}
                  </div>
                  <div style={{ fontFamily: G.mono, fontSize: 9, color: G.muted }}>Catégorie assignée par votre DS Admin</div>
                </div>
                <div>
                  <div style={{ fontFamily: G.mono, fontSize: 8, letterSpacing: '.12em', textTransform: 'uppercase', color: G.muted, marginBottom: 8 }}>Niveau de championnat</div>
                  <select
                    value={teamLevel}
                    onChange={e => { setTeamLevel(e.target.value); setTeamLevelSaving('idle') }}
                    style={{
                      width: '100%', background: G.bg2, border: `1px solid ${G.border}`,
                      padding: '12px 16px', color: G.text, fontFamily: G.mono, fontSize: 13,
                      outline: 'none', boxSizing: 'border-box', cursor: 'pointer', transition: 'border-color .15s',
                    }}
                    onFocus={e => e.target.style.borderColor = G.goldBdr}
                    onBlur={e => e.target.style.borderColor = G.border}
                  >
                    <option value="">— Non défini —</option>
                    {(COMPETITION_LEVELS_BY_CATEGORY[user?.managed_category] || ALL_COMPETITION_LEVELS).map(l => <option key={l} value={l}>{l}</option>)}
                  </select>
                </div>
                <button
                  onClick={handleTeamLevelSave}
                  disabled={teamLevelSaving === 'saving'}
                  style={{
                    alignSelf: 'flex-start', fontFamily: G.mono, fontSize: 9, letterSpacing: '.1em', textTransform: 'uppercase',
                    padding: '10px 16px', whiteSpace: 'nowrap', transition: 'all .15s', fontWeight: 600,
                    cursor: teamLevelSaving === 'saving' ? 'not-allowed' : 'pointer',
                    opacity: teamLevelSaving === 'saving' ? .6 : 1,
                    border: teamLevelSaving === 'saved' ? '1px solid rgba(34,197,94,0.35)' : `1px solid ${G.goldBdr}`,
                    background: teamLevelSaving === 'saved' ? 'rgba(34,197,94,0.07)' : G.goldBg,
                    color: teamLevelSaving === 'saved' ? '#4ade80' : G.gold,
                  }}
                >
                  {teamLevelSaving === 'saving' ? '...' : teamLevelSaving === 'saved' ? '✓ Enregistré' : 'Enregistrer'}
                </button>
                <div style={{ fontFamily: G.mono, fontSize: 9, color: G.muted }}>
                  Ce niveau s'affiche sur les cartes des joueurs de ton équipe {user.managed_category}.
                </div>
              </div>
              {/* Preview carte */}
              {!isMobile && (
                <div style={{ flexShrink: 0 }}>
                  <div style={{ fontFamily: G.mono, fontSize: 8, letterSpacing: '.12em', textTransform: 'uppercase', color: G.muted, marginBottom: 8, textAlign: 'center' }}>
                    Aperçu carte
                  </div>
                  <div style={{ width: 247, height: 439, overflow: 'hidden' }}>
                    <div style={{ transform: 'scale(0.65)', transformOrigin: 'top left', width: 380 }}>
                      <PlayerCardPreview
                        primaryColor={clubColors.primary}
                        secondaryColor={clubColors.secondary}
                        competitionLevel={teamLevel}
                        clubName={user?.club_name || ''}
                        logoUrl={null}
                        season={currentSeason}
                      />
                    </div>
                  </div>
                </div>
              )}
            </div>
          </Section>
        )}

        {/* Mon club — nom + logo, éditables par le coach solo. Masqué pour les
            membres d'un club : leur club (nom + logo) est géré par le DS Admin
            via ClubSettings. */}
        {!isClubMember && (
        <Section title="Mon club" accent={G.border}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            {/* Champ nom du club */}
            <div>
              <label style={{ display: 'block', fontFamily: G.mono, fontSize: 8, letterSpacing: '.14em', textTransform: 'uppercase', color: G.muted, marginBottom: 8 }}>
                Nom du club
              </label>
              <input
                type="text"
                value={clubName}
                onChange={e => setClubName(e.target.value)}
                placeholder="Ex : AS Saint-Étienne"
                style={{ width: '100%', background: G.bg2, border: `1px solid ${G.border}`, padding: '12px 16px', color: G.text, fontFamily: G.mono, fontSize: 12, outline: 'none', boxSizing: 'border-box', transition: 'border-color .15s' }}
                onFocus={e => e.target.style.borderColor = G.goldBdr}
                onBlur={e => e.target.style.borderColor = G.border}
              />
            </div>

            {/* Aperçu logo */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 16, padding: '16px', background: 'rgba(255,255,255,0.02)', border: `1px solid ${G.border}` }}>
              <div style={{ width: 56, height: 56, flexShrink: 0, background: G.goldBg, border: `1px solid ${G.goldBdr}`, display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden' }}>
                {logoUrl ? (
                  <img src={logoUrl} alt="Logo club" style={{ width: '100%', height: '100%', objectFit: 'contain' }}
                    onError={e => { e.target.style.display = 'none' }} />
                ) : (
                  <ImageIcon size={20} color={G.gold} />
                )}
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontFamily: G.mono, fontSize: 8, letterSpacing: '.12em', textTransform: 'uppercase', color: G.muted, marginBottom: 4 }}>
                  {logoUrl ? 'Logo actuel' : 'Aucun logo'}
                </div>
                <div style={{ fontFamily: G.mono, fontSize: 10, color: logoUrl ? G.text : G.muted, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {logoUrl || 'Colle l\'URL de ton logo ci-dessous'}
                </div>
              </div>
              {logoUrl && (
                <button onClick={() => setLogoUrl('')} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 4, flexShrink: 0 }}>
                  <X size={14} color={G.muted} />
                </button>
              )}
            </div>

            {/* Champ URL logo */}
            <div>
              <label style={{ display: 'block', fontFamily: G.mono, fontSize: 8, letterSpacing: '.14em', textTransform: 'uppercase', color: G.muted, marginBottom: 8 }}>
                URL du logo
              </label>
              <input
                type="url"
                value={logoUrl}
                onChange={e => setLogoUrl(e.target.value)}
                placeholder="https://monclub.fr/logo.png"
                style={{ width: '100%', background: G.bg2, border: `1px solid ${G.border}`, padding: '12px 16px', color: G.text, fontFamily: G.mono, fontSize: 12, outline: 'none', boxSizing: 'border-box', transition: 'border-color .15s' }}
                onFocus={e => e.target.style.borderColor = G.goldBdr}
                onBlur={e => e.target.style.borderColor = G.border}
              />
              <p style={{ fontFamily: G.mono, fontSize: 9, color: G.muted, marginTop: 6 }}>
                PNG, SVG ou JPG recommandé. Fond transparent idéal.
              </p>
            </div>

            {clubError && (
              <p style={{ fontFamily: G.mono, fontSize: 10, color: T.red, margin: 0 }}>{clubError}</p>
            )}

            {/* Bouton sauvegarder (nom + logo en un seul appel) */}
            <button onClick={handleSaveClub} disabled={clubSaving} style={{
              alignSelf: 'flex-start', padding: '10px 20px',
              background: clubSaved ? 'rgba(34,197,94,0.15)' : G.gold,
              border: clubSaved ? '1px solid rgba(34,197,94,0.35)' : 'none',
              color: clubSaved ? '#22c55e' : '#0f0f0d',
              fontFamily: G.mono, fontSize: 9, letterSpacing: '.12em',
              textTransform: 'uppercase', fontWeight: 700,
              cursor: clubSaving ? 'not-allowed' : 'pointer',
              opacity: clubSaving ? 0.7 : 1,
              transition: 'all .2s',
            }}>
              {clubSaving ? 'Enregistrement...' : clubSaved ? '✓ Club enregistré' : 'Enregistrer'}
            </button>
          </div>
        </Section>
        )}

        {/* Saisons archivées */}
        <Section title="Saisons archivées" accent={G.border}>
          {archivedSeasons.length === 0 ? (
            <p style={{ fontFamily: G.mono, fontSize: 11, color: G.muted, margin: 0 }}>
              Aucune saison archivée pour l'instant.
            </p>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 1, background: G.border }}>
              {archivedSeasons.map(season => (
                <button
                  key={season}
                  onClick={() => { setSelectedSeason(season); navigate('/dashboard/players') }}
                  style={{
                    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                    padding: '14px 16px', background: G.bg2, border: 'none',
                    cursor: 'pointer', textAlign: 'left', width: '100%',
                    transition: 'background .1s',
                  }}
                  onMouseEnter={e => e.currentTarget.style.background = 'rgba(79,142,247,0.05)'}
                  onMouseLeave={e => e.currentTarget.style.background = G.bg2}
                >
                  <div>
                    <div style={{ fontFamily: G.mono, fontSize: 13, color: G.text, marginBottom: 2 }}>{season}</div>
                    <div style={{ fontFamily: G.mono, fontSize: 9, letterSpacing: '.1em', textTransform: 'uppercase', color: G.muted }}>
                      Lecture seule
                    </div>
                  </div>
                  <ChevronRight size={14} color={G.muted} />
                </button>
              ))}
            </div>
          )}
        </Section>

        {/* Aperçu carte joueur — coach solo (pas de managed_category) */}
        {!isClubMember && !isMobile && (
          <Section title="Aperçu de la carte joueur" accent={G.border}>
            <div style={{ display: 'flex', justifyContent: 'center' }}>
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 10 }}>
                <div style={{ fontFamily: G.mono, fontSize: 9, color: G.muted, letterSpacing: '.1em', textTransform: 'uppercase' }}>
                  Aperçu avec les couleurs de ton club
                </div>
                <div style={{ width: 247, height: 439, overflow: 'hidden' }}>
                  <div style={{ transform: 'scale(0.65)', transformOrigin: 'top left', width: 380 }}>
                    <PlayerCardPreview
                      primaryColor={clubColors.primary}
                      secondaryColor={clubColors.secondary}
                      competitionLevel=""
                      clubName={clubName || user?.club_name || ''}
                      logoUrl={null}
                      season={currentSeason}
                    />
                  </div>
                </div>
              </div>
            </div>
          </Section>
        )}

        {/* Analyses */}
        {/* A9 · le bloc Analyses était masqué à un membre de club : il ne
            voyait jamais le quota de son club. */}
        {!estVraiFree(user) && creditsData !== null && (
          <div style={{ background: G.bg2, border: `1px solid ${G.border}`, borderTop: `2px solid ${G.gold}`, padding: '28px' }}>
            <div style={{ fontFamily: G.mono, fontSize: 9, letterSpacing: '.18em', textTransform: 'uppercase', color: G.gold, display: 'flex', alignItems: 'center', gap: 8, marginBottom: 20 }}>
              <span style={{ width: 12, height: 1, background: G.gold, display: 'inline-block' }} />Analyses ce mois
            </div>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12, marginBottom: 20 }}>
              <div style={{ display: 'flex', gap: 24 }}>
                <span style={{ fontFamily: G.mono, fontSize: 12, color: G.text }}>
                  {creditsData.analyses_quota_used} / {creditsData.analyses_quota} utilisées
                </span>
                <span style={{ fontFamily: G.mono, fontSize: 12, color: G.muted }}>
                  En réserve : {creditsData.analyses_reserve}
                </span>
              </div>
            </div>
            <button
              onClick={() => setShowBuyAnalysesModal(true)}
              style={{
                background: G.gold, border: 'none', cursor: 'pointer',
                padding: '10px 20px', fontFamily: G.mono, fontSize: 10,
                fontWeight: 700, letterSpacing: '.1em', textTransform: 'uppercase',
                color: '#0f0f0d',
              }}
            >
              Acheter des analyses
            </button>
          </div>
        )}

        {/* Abonnement · BANDEAU-SANS-QUOTA-001 · l'ancre `#abonnement` est la
            DESTINATION d'un bouton qui promet une offre. Sans elle, le coach
            arrivait en haut de cette page, sous six blocs sans rapport, et rien
            ne lui rappelait pourquoi il etait la. */}
        {!isClubMember ? (
          <div id="abonnement" style={{ background: G.bg2, border: `1px solid ${G.border}`, borderTop: `2px solid ${G.border}`, padding: '28px', scrollMarginTop: 24 }}>
            <div style={{ fontFamily: G.mono, fontSize: 9, letterSpacing: '.18em', textTransform: 'uppercase', color: G.gold, display: 'flex', alignItems: 'center', gap: 8, marginBottom: 20 }}>
              <span style={{ width: 12, height: 1, background: G.gold, display: 'inline-block' }} />Abonnement
            </div>
            <SubscriptionManagement onTrialStatusChange={setIsTrialing} />
          </div>
        ) : (
          <div id="abonnement" style={{ background: G.bg2, border: `1px solid ${G.border}`, borderTop: `2px solid ${G.gold}`, padding: '28px', scrollMarginTop: 24 }}>
            <div style={{ fontFamily: G.mono, fontSize: 9, letterSpacing: '.18em', textTransform: 'uppercase', color: G.gold, display: 'flex', alignItems: 'center', gap: 8, marginBottom: 16 }}>
              <span style={{ width: 12, height: 1, background: G.gold, display: 'inline-block' }} />Abonnement
            </div>
            <p style={{ fontFamily: G.mono, fontSize: 12, color: G.muted, lineHeight: 1.7, margin: 0 }}>
              Votre accès est géré par votre club.<br />
              Contactez votre directeur sportif pour toute question.
            </p>
          </div>
        )}

        {/* Supprimer compte */}
        <div style={{ padding: '16px 28px', display: 'flex', justifyContent: 'flex-end', background: G.bg2, border: `1px solid ${G.border}` }}>
          <button onClick={() => setShowDeleteModal(true)} style={{
            background: 'none', border: 'none', cursor: 'pointer',
            fontFamily: G.mono, fontSize: 9, letterSpacing: '.1em', textTransform: 'uppercase',
            color: 'rgba(239,68,68,0.45)', display: 'flex', alignItems: 'center', gap: 6, padding: 0,
            transition: 'color .15s',
          }}
            onMouseEnter={e => e.currentTarget.style.color = '#ef4444'}
            onMouseLeave={e => e.currentTarget.style.color = 'rgba(239,68,68,0.45)'}>
            <Trash2 size={10} /> Supprimer mon compte
          </button>
        </div>
      </div>

      {/* Modale mot de passe */}
      {showPwdModal && <ChangePasswordModal onClose={() => setShowPwdModal(false)} />}

      {/* Modale achat analyses */}
      {showBuyAnalysesModal && <BuyAnalysesModal onClose={() => setShowBuyAnalysesModal(false)} hasCredits />}

      {/* Modale identité */}
      {showIdentityModal && (
        <CoachIdentityModal
          onClose={() => setShowIdentityModal(false)}
          onSave={handleIdentitySaved}
          initialData={coachIdentity}
        />
      )}

      {/* Modale suppression */}
      {showDeleteModal && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.8)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: 20 }}>
          <div style={{ background: T.dark, width: '100%', maxWidth: 440, border: `1px solid rgba(239,68,68,0.3)`, borderTop: `2px solid #ef4444` }}>
            <div style={{ padding: '20px 24px', borderBottom: `1px solid ${G.border}`, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <AlertTriangle size={16} color="#ef4444" />
                <span style={{ fontFamily: G.display, fontSize: 18, textTransform: 'uppercase', color: '#f5f2eb' }}>Supprimer le compte</span>
              </div>
              <button onClick={() => { setShowDeleteModal(false); setDeleteConfirm('') }} style={{ background: 'none', border: 'none', cursor: 'pointer' }}>
                <X size={16} color={G.muted} />
              </button>
            </div>
            <div style={{ padding: '24px' }}>
              <div style={{ padding: '14px 16px', background: 'rgba(239,68,68,0.06)', border: `1px solid rgba(239,68,68,0.15)`, marginBottom: 24 }}>
                <p style={{ fontFamily: G.mono, fontSize: 11, color: 'rgba(245,242,235,0.7)', lineHeight: 1.7, margin: 0 }}>
                  Votre compte sera désactivé immédiatement. Toutes vos données seront conservées pendant <strong style={{ color: '#f5f2eb' }}>30 jours</strong> et vous recevrez un email pour récupérer votre compte si vous changez d'avis.
                </p>
              </div>
              <div style={{ marginBottom: 20 }}>
                <div style={{ fontFamily: G.mono, fontSize: 8, letterSpacing: '.16em', textTransform: 'uppercase', color: G.muted, marginBottom: 10 }}>
                  Tapez <strong style={{ color: '#ef4444' }}>SUPPRIMER</strong> pour confirmer
                </div>
                <input type="text" value={deleteConfirm} onChange={e => setDeleteConfirm(e.target.value)}
                  placeholder="SUPPRIMER"
                  style={{
                    width: '100%', background: 'rgba(239,68,68,0.04)',
                    border: `1px solid ${deleteConfirm === 'SUPPRIMER' ? '#ef4444' : 'rgba(239,68,68,0.15)'}`,
                    padding: '12px 16px', color: '#f5f2eb', fontFamily: G.mono, fontSize: 13,
                    outline: 'none', boxSizing: 'border-box', letterSpacing: '.06em',
                  }} />
              </div>
              {deleteError && (
                <div style={{ marginBottom: 12, padding: '10px 14px', background: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.2)' }}>
                  <p style={{ fontFamily: G.mono, fontSize: 10, color: '#ef4444', margin: 0 }}>{deleteError}</p>
                </div>
              )}
              <div style={{ display: 'flex', gap: 10 }}>
                <button onClick={() => { setShowDeleteModal(false); setDeleteConfirm(''); setDeleteError('') }} style={{
                  flex: 1, padding: '11px', background: 'transparent',
                  border: `1px solid ${G.border}`, fontFamily: G.mono, fontSize: 9,
                  letterSpacing: '.1em', textTransform: 'uppercase', color: G.muted, cursor: 'pointer',
                }}>Annuler</button>
                <button onClick={handleDeleteAccount} disabled={deleteConfirm !== 'SUPPRIMER' || deleteLoading} style={{
                  flex: 2, padding: '11px',
                  background: deleteConfirm === 'SUPPRIMER' ? '#ef4444' : 'rgba(239,68,68,0.2)',
                  border: 'none', fontFamily: G.mono, fontSize: 9,
                  letterSpacing: '.1em', textTransform: 'uppercase',
                  color: deleteConfirm === 'SUPPRIMER' ? '#fff' : 'rgba(239,68,68,0.4)',
                  cursor: deleteConfirm === 'SUPPRIMER' ? 'pointer' : 'not-allowed',
                  fontWeight: 700, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
                }}>
                  {deleteLoading ? 'Suppression...' : <><Trash2 size={11} /> Supprimer définitivement</>}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </DashboardLayout>
  )
}
